/** The part of `navigator.serviceWorker` used here (so tests can supply a fake). */
export type WorkerContainer = Pick<ServiceWorkerContainer, 'controller' | 'addEventListener'>;

/** The part of a service worker registration used here (so tests can supply a fake). */
export type UpdatableRegistration = Pick<ServiceWorkerRegistration, 'update'>;

/** The part of `document` used by the update checks (so tests can supply a fake). */
export type VisibilityPage = Pick<Document, 'visibilityState'> & {
  addEventListener(type: 'visibilitychange', listener: () => void): void;
};

/** The part of an event target used by the gesture tracker (so tests can supply a fake). */
export interface PointerEventTarget {
  addEventListener(type: 'pointerdown' | 'pointerup' | 'pointercancel' | 'blur', listener: () => void): void;
}

/** How often a long-lived page asks the registration for a new worker version (§2.1.9). */
export const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

export interface GestureTracker {
  /** True while at least one pointer is down (a drag may be in progress). */
  active(): boolean;
  /** Run `callback` as soon as every pointer is up; replaces any callback not yet run. */
  onIdle(callback: () => void): void;
}

/**
 * Tracks whether a pointer is down anywhere on the page. A reload must never interrupt a drag,
 * so the worker-update reload is deferred while a pointer is down and runs as soon as the
 * gesture ends — a release, a cancelled gesture, a window blur and the page becoming hidden
 * all end it (the timeline cancels its drag on those same interruption events, so a deferred
 * reload must not stay pending until the next pointer press).
 */
export function trackPointerGestures(target: PointerEventTarget, page?: VisibilityPage): GestureTracker {
  let pointersDown = 0;
  let pending: (() => void) | null = null;
  const flush = (): void => {
    if (pointersDown > 0 || pending === null) return;
    const run = pending;
    pending = null;
    run();
  };
  target.addEventListener('pointerdown', () => {
    pointersDown += 1;
  });
  const up = (): void => {
    pointersDown = Math.max(0, pointersDown - 1);
    flush();
  };
  // The gesture every pointer belonged to was interrupted: nothing is down any more.
  const interrupt = (): void => {
    pointersDown = 0;
    flush();
  };
  target.addEventListener('pointerup', up);
  target.addEventListener('pointercancel', up);
  target.addEventListener('blur', interrupt);
  page?.addEventListener('visibilitychange', () => {
    if (page.visibilityState === 'hidden') interrupt();
  });
  return {
    active: () => pointersDown > 0,
    onIdle(callback) {
      pending = callback;
    },
  };
}

/**
 * A new service worker replaces the files that the old version precached, so the lazily loaded parts of a page
 * that is still open would no longer be found. Reloads once when a new worker takes over a page that was already
 * controlled by an old one. On the first visit the first worker claims the page without a reload.
 * While a pointer gesture is in progress the reload waits for the gesture to end (§2.1.9).
 */
export function reloadWhenWorkerUpdates(
  container: WorkerContainer | undefined,
  reload: () => void,
  gestures?: GestureTracker,
): void {
  if (container === undefined) return;
  let controlled = container.controller !== null;
  let reloading = false;
  container.addEventListener('controllerchange', () => {
    if (!controlled) {
      controlled = true;
      return;
    }
    if (reloading) return;
    reloading = true;
    if (gestures !== undefined && gestures.active()) gestures.onIdle(reload);
    else reload();
  });
}

/**
 * The browser checks for a new worker only on navigation, so a long-lived tab would never see
 * one. Also ask the registration for updates whenever the page becomes visible again and every
 * 30 minutes (§2.1.9). A failed check (for example offline) is ignored; the next one retries.
 */
export function scheduleWorkerUpdateChecks(
  getRegistration: (() => Promise<UpdatableRegistration | undefined>) | undefined,
  page: VisibilityPage,
  setInterval: (handler: () => void, intervalMs: number) => void,
): void {
  if (getRegistration === undefined) return;
  const check = (): void => {
    getRegistration()
      .then((registration) => registration?.update())
      .catch(() => undefined);
  };
  page.addEventListener('visibilitychange', () => {
    if (page.visibilityState === 'visible') check();
  });
  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
}

/**
 * Several reload blockers as one (2026-10b §5.4): the reload waits while any of them is
 * active, and runs as soon as all are idle. Each blocker re-checks the others when it goes
 * idle, so "picker dirty" and "pointer down" can end in any order.
 */
export function combineGestureTrackers(...trackers: readonly GestureTracker[]): GestureTracker {
  let pending: (() => void) | null = null;
  const attempt = (): void => {
    if (pending === null || trackers.some((tracker) => tracker.active())) return;
    const run = pending;
    pending = null;
    run();
  };
  return {
    active: () => trackers.some((tracker) => tracker.active()),
    onIdle(callback) {
      pending = callback;
      for (const tracker of trackers) tracker.onIdle(attempt);
      attempt();
    },
  };
}
